import React, { useEffect, useRef, useState } from 'react';
// import * as THREE from 'three';
import {
  Euler,
  Mesh,
  MeshBasicMaterial,
  Plane,
  Vector2,
  Vector2Tuple,
  Vector3,
  Vector3Tuple,
} from 'three';

import { Billboard, Text } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useEventCallback } from '@spazious/react-hooks';
import { useDrag } from '@use-gesture/react';

import { ShapeUtils } from '../../extras/index.js';

const _size = new Vector3();
const _campos = new Vector3();
const _pos = new Vector3();

interface CanvasLabelProps extends React.ComponentProps<typeof Text> {
  children: React.ReactNode;
  position: Vector3Tuple | Vector3;
  size?: Vector2Tuple;
  padding?: Vector2Tuple;
  bgOpacity?: number;
  bgColor?: string;
  onDrag?: (payload: { position: Vector2; last: boolean; first: boolean }) => void;
  draggable?: boolean;
  billboard?: boolean;
  cornerRadius?: number;
  /** Background shape. Defaults to a (rounded) rectangle. */
  shape?: 'rect' | 'circle';
  /** Color of the border ring. When unset (or `borderWidth` is 0) no border is drawn. */
  borderColor?: string;
  /** Border ring thickness in world units (scaled with distance like the rest). */
  borderWidth?: number;
  borderOpacity?: number;
  onSizeUpdate?: (size: Vector2Tuple) => void;
}

export const CanvasLabel: React.FC<CanvasLabelProps> = ({
  children,
  position,
  size,
  padding = [0, 0],
  rotation = new Euler(0, 0, 0),
  fontSize = 0.4,
  bgColor = 'white',
  bgOpacity = 1,
  fontWeight = 400,
  color = 'black',
  onDrag,
  draggable = false,
  billboard = false,
  cornerRadius = 0,
  shape = 'rect',
  borderColor,
  borderWidth = 0,
  borderOpacity = 1,
  onSizeUpdate,
  ...restProps
}) => {
  const { controls, camera, pointer, raycaster } = useThree();
  const [scale, setScale] = useState(1);

  const bind = useDrag(({ event, down, last, first }) => {
    event.stopPropagation();
    const plane = new Plane(new Vector3(0, 1, 0), 0);
    const pos = new Vector3();
    raycaster.setFromCamera(pointer, camera);
    raycaster.ray.intersectPlane(plane, pos);
    onDrag?.({ position: new Vector2(pos.x, pos.z), last, first });

    if (controls) {
      (controls as any).enabled = !down;
    }
  });

  const [textSize, setTextSize] = useState<Vector2Tuple>([0, 0]);

  const textRef = useRef<Text>();
  const onSyncCallback = useEventCallback(() => {
    if (textRef.current) {
      (textRef.current as any).geometry.boundingBox.getSize(_size);
      setTextSize([_size.x, _size.y]);
      onSizeUpdate?.([_size.x, _size.y]);
    }
  });

  useEffect(() => {
    const updateScale = () => {
      if (textRef.current && camera) {
        camera.getWorldPosition(_campos);
        (textRef.current as unknown as Mesh).getWorldPosition(_pos);
        const distance = Math.round(_pos.distanceTo(_campos));
        setScale(distance * 0.03);
      }
    };
    updateScale();

    controls?.addEventListener('rest', updateScale);
    return () => {
      controls?.removeEventListener('rest', updateScale);
    };
  }, [camera, controls]);

  const labelSize = size ? [size[0] * scale, size[1] * scale] : textSize;

  const props = {
    position,
    rotation,
    ...((draggable ? bind() : {}) as any),
  };

  const bgWidth = labelSize[0] + padding[0] * 2 * scale;
  const bgHeight = labelSize[1] + padding[1] * 2 * scale;
  const circleRadius = Math.max(bgWidth, bgHeight) / 2;
  const hasBorder = !!borderColor && borderWidth > 0;
  const borderSize = borderWidth * scale;

  const label = (
    <>
      <Text
        ref={textRef}
        anchorX="center"
        anchorY="middle"
        color={color}
        fontSize={fontSize * scale}
        fontWeight={fontWeight}
        lineHeight={1}
        material={new MeshBasicMaterial({ color: color as any, depthTest: false })}
        material-toneMapped={false}
        renderOrder={1010}
        onSync={onSyncCallback}
        {...restProps}
      >
        {children}
      </Text>

      {hasBorder && (
        <mesh renderOrder={1008}>
          {shape === 'circle' ? (
            <circleGeometry args={[circleRadius + borderSize, 64]} />
          ) : (
            <shapeGeometry
              args={[
                ShapeUtils.roundedRectShape(
                  bgWidth + borderSize * 2,
                  bgHeight + borderSize * 2,
                  (cornerRadius + borderWidth) * scale
                ),
              ]}
            />
          )}
          <meshStandardMaterial
            transparent
            color={borderColor}
            depthTest={false}
            opacity={borderOpacity}
          />
        </mesh>
      )}

      <mesh renderOrder={1009}>
        {shape === 'circle' ? (
          <circleGeometry args={[circleRadius, 64]} />
        ) : (
          <shapeGeometry
            args={[ShapeUtils.roundedRectShape(bgWidth, bgHeight, cornerRadius * scale)]}
          />
        )}
        <meshStandardMaterial transparent color={bgColor} depthTest={false} opacity={bgOpacity} />
      </mesh>
    </>
  );

  return billboard ? <Billboard {...props}>{label}</Billboard> : <group {...props}>{label}</group>;
};
